import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Logo } from 'donor/portal/pages/Routes';
import { COLORS } from '../../design-system/settings/variables/Colors';
import {
  AnswersBySectionIdResponse,
  getAnswersBySectionId,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { useGraphQLRequest } from '../hooks/useApiRequest';
import { useClient } from '../../apollo';
import { answersBySection } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { useLocation } from 'react-router-dom';
import TrueImpactLoading from '../../components/TrueImpactLoading';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import { useQueryString } from '../hooks/useQueryString';
import { FeedbackDashboardCardProps } from '../../components/Feedback/FeedbackDashboardCard';
import {
  FeedbackApi,
  feedbackMapFromApi,
  getFeedback,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { useIr } from 'shared/apollo/pageHooks/useIr';
import FeedbackImpactReceipt from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactReceipt';
import { ImpactReceipt } from 'donor/portal/pages/DonorReceipt/utils';
import toCamelCase from 'camelcase-keys';
import { IndicatorProvider } from '../../providers/IndicatorProvider';

const StyledDiv = styled.div`
  .header {
    background-color: ${COLORS.reportBuilderGray};
  }
  .logo {
    position: unset;
  }
  .error-message {
    text-align: center;
    padding: ${SPACERS.large};
  }
  .impact-receipt {
    .impact-receipt-report-header {
    }
    .impact-receipt-report-content {
      margin: 0 auto;
      max-width: 900px;
    }
  }
`;

const Header = () => {
  return (
    <div className="header">
      <Logo className="logo" />
    </div>
  );
};

const Error = () => {
  return (
    <StyledDiv>
      <Header />
      <TrueImpactText color="critical" className="error-message">
        Unable to load the report.
      </TrueImpactText>
    </StyledDiv>
  );
};

const Loading = () => {
  return (
    <StyledDiv>
      <Header />
      <TrueImpactLoading />
    </StyledDiv>
  );
};

const PublicReportInner: React.FC = () => {
  const client = useClient();
  const query = useQueryString();
  const token = query.get('token');
  const { pathname } = useLocation();
  // Check if user is authenticated via CN Auth0 (Apollo provider handles the token)
  const isCnAuth0User = !!localStorage.getItem('cn_ti_token');
  const [requestState, makeRequest] = useGraphQLRequest<
    AnswersBySectionIdResponse
  >();
  const [feedbackRequestState, makeFeedbackRequest] = useGraphQLRequest<{
    reportsTodo: FeedbackApi[];
  }>();
  const [feedback, setFeedback] = useState<FeedbackDashboardCardProps>();
  const [allocationId, setAllocationId] = useState<number>();
  const [investmentId, setInvestmentId] = useState<number>();

  const reportIdRef = useRef<string | null>(
    pathname.split('/report/').pop() || null,
  );
  const reportId = reportIdRef.current;

  // Only fetch IR data when investmentId is available
  const { data: irData } = useIr(investmentId, allocationId);

  const ir: ImpactReceipt | undefined =
    irData && Object.keys(irData).length > 0
      ? toCamelCase(irData, { deep: true })
      : undefined;

  useEffect(() => {
    // Make request if we have client, reportId, and either a URL token OR CN Auth0 user
    if (client && reportId && (token || isCnAuth0User)) {
      makeRequest(getAnswersBySectionId(client, reportId));
    }
  }, [client, reportId, token, isCnAuth0User]);

  useEffect(() => {
    client &&
      reportId &&
      makeFeedbackRequest(
        getFeedback(
          client,
          undefined,
          undefined,
          reportId,
          undefined,
          undefined,
        ),
      );
  }, [client, reportId]);

  useEffect(() => {
    if (feedbackRequestState.type === 'REQUEST_SUCCESS') {
      const feedbacks = feedbackMapFromApi(
        feedbackRequestState.data.reportsTodo,
      ).feedbackRows;
      setFeedback(feedbacks[0]);
      // Safely Detects the allocation ID and the investment ID, for V3 Reports
      try {
        const rawAllocationId =
          feedbackRequestState.data.reportsTodo?.[0]?.allocations?.[0]?.dbId;
        const rawInvestmentId =
          feedbackRequestState.data.reportsTodo?.[0]?.allocations?.[0]
            ?.investment?.dbId;
        setAllocationId(rawAllocationId);
        setInvestmentId(rawInvestmentId);
      } catch (error) {
        console.error(error);
      }
    }
  }, [feedbackRequestState.type, setFeedback]);

  const answers = useMemo(
    () => answersBySection(requestState.data?.allNpoReportAnswers || []),
    [requestState],
  );

  if (!client || !reportId || (!token && !isCnAuth0User)) {
    return <Error />;
  }

  if (
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START' ||
    feedbackRequestState.type === 'REQUEST_INIT' ||
    feedbackRequestState.type === 'REQUEST_START'
  ) {
    return <Loading />;
  }

  if (
    !feedback ||
    requestState.type === 'REQUEST_ERROR' ||
    feedbackRequestState.type === 'REQUEST_ERROR'
  ) {
    return <Error />;
  }

  return (
    <StyledDiv>
      <Header />
      <div className="impact-receipt">
        <FeedbackImpactReceipt
          isThirdParty={true}
          programName={feedback.title || feedback.portfolioName}
          reportId={reportId}
          answers={answers}
          feedback={feedback}
          isLegacy={ir?.report?.isLegacy}
          report={ir?.report}
          ir={ir}
        />
      </div>
    </StyledDiv>
  );
};

const PublicReport: React.FC = () => {
  const query = useQueryString();
  const token = query.get('token') || undefined;
  return (
    <IndicatorProvider publicToken={token}>
      <PublicReportInner />
    </IndicatorProvider>
  );
};

export default PublicReport;
